import { ethers } from 'ethers';
import { LEVER_SWAP_CONFIG } from './swapConfig';
import { fetchSushiSwapData } from './sushiApi';
import { getAllowanceStatus } from './allowance';
import { extractExpectedTokenOutAmount, toDisplayAmount } from './extractSwapAmount';
import { applyBuyFactor, applySwapMultiplier, ERC20_ABI, formatTokenFloor, normalizeSwapMultiplier, safeNormalize, STARTAX_SWAP_ABI } from './swapUtils';
import { mergeValidationResults, validateApprovalChecks, validatePreparedSwap } from './validateSwap';
import { buildEthBalanceCheck, throwInsufficientBalanceError } from './balanceChecks';

export async function buildBuySwapDryRun({ signer, walletAddress, swapMultiplier = LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER }) {
  if (!signer) throw new Error('Missing wallet signer.');
  const provider = signer.provider;
  const accountAddress = safeNormalize(walletAddress || (await signer.getAddress()));

  const starTax = new ethers.Contract(safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS), STARTAX_SWAP_ABI, provider);
  const selectedSwapMultiplier = normalizeSwapMultiplier(swapMultiplier);
  const baseWETH = await starTax.getTransactionMinimumInWETH();
  const standardAmountWETH = applyBuyFactor(baseWETH);
  const amountWETH = applySwapMultiplier(standardAmountWETH, selectedSwapMultiplier);

  const weth = new ethers.Contract(safeNormalize(LEVER_SWAP_CONFIG.WETH_ADDRESS), ERC20_ABI, provider);
  const wethBalance = await weth.balanceOf(accountAddress);
  const useNative = wethBalance < BigInt(amountWETH);
  const tokenIn = useNative ? LEVER_SWAP_CONFIG.NATIVE_ADDRESS : safeNormalize(LEVER_SWAP_CONFIG.WETH_ADDRESS);
  const tokenOut = safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS);

  const { url, data: swapData } = await fetchSushiSwapData({
    tokenIn,
    tokenOut,
    amount: amountWETH.toString(),
    sender: accountAddress
  });

  const txData = swapData.tx;
  const baseValidation = validatePreparedSwap({ mode: 'BUY', walletAddress: accountAddress, txData, tokenIn, tokenOut, amount: amountWETH.toString() });

  const allowanceChecks = [];
  if (!useNative) {
    allowanceChecks.push(await getAllowanceStatus({
      provider,
      tokenAddress: LEVER_SWAP_CONFIG.WETH_ADDRESS,
      ownerAddress: accountAddress,
      spender: LEVER_SWAP_CONFIG.PERMIT2_ADDRESS,
      requiredAmount: amountWETH
    }));
    if (txData?.to) {
      allowanceChecks.push(await getAllowanceStatus({
        provider,
        tokenAddress: LEVER_SWAP_CONFIG.WETH_ADDRESS,
        ownerAddress: accountAddress,
        spender: txData.to,
        requiredAmount: amountWETH
      }));
    }
  }

  const approvalValidation = validateApprovalChecks({ allowanceChecks });
  const balanceCheck = await buildEthBalanceCheck({
    provider,
    accountAddress,
    mode: 'BUY',
    txData,
    valueRaw: txData?.value || (useNative ? amountWETH.toString() : '0'),
    approvalCount: allowanceChecks.filter((item) => !item.isEnough).length
  });

  if (!balanceCheck.ok) {
    throw throwInsufficientBalanceError({
      code: 'INSUFFICIENT_OPTIMISM_ETH_BALANCE',
      message: 'Insufficient ETH balance on Optimism for BUY swap.',
      balanceCheck
    });
  }

  const validation = mergeValidationResults(baseValidation, approvalValidation);

  const expectedTokenOutBN = extractExpectedTokenOutAmount(swapData);

  return {
    dryRun: true,
    mode: 'BUY',
    walletAddress: accountAddress,
    sushiUrl: url,
    txData,
    tokenIn,
    tokenOut,
    useNative,
    baseAmountRaw: baseWETH.toString(),
    standardAmountRaw: standardAmountWETH.toString(),
    swapMultiplier: selectedSwapMultiplier,
    amountInRaw: amountWETH.toString(),
    amountInDisplay: formatTokenFloor(amountWETH, 18),
    expectedTaxOutRaw: expectedTokenOutBN?.toString() || null,
    lastSwapAmountDisplay: toDisplayAmount(expectedTokenOutBN),
    allowanceChecks,
    balanceCheck,
    validation,
    swapData
  };
}
